'use client';

import {
  Badge,
  Box,
  Button,
  CloseButton,
  Collapsible,
  Dialog,
  Flex,
  Heading,
  HStack,
  Portal,
  SimpleGrid,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  AlertCircle,
  ArrowRight,
  BarChart3,
  Bot,
  Cloud,
  CheckCircle2,
  ChevronDown,
  FileText,
  GitBranch,
  Globe,
  Megaphone,
  MousePointerClick,
  Search,
  Server,
  ShoppingBag,
  Youtube,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { IntelligenceSetupDialog } from '@/components/onboarding/IntelligenceSetupDialog';
import { IntegrationSetupProvider } from '@/components/shared/integrations/IntegrationSetupContext';
import {
  getWorkspaceModuleCatalog,
  RECOMMENDED_WORKSPACE_MODULE_IDS,
} from '@/lib/projects/module-catalog';
import type { IntelligenceStatusResponse } from '@/types/onboarding';
import { useAuth } from '@/app/auth-provider';
import { AttuneMcpIntegration } from '@/components/shared/integrations/AttuneMcpIntegration';
import { CustomMcpIntegration } from '@/components/shared/integrations/CustomMcpIntegration';
import {
  EnterpriseAnalyticsIntegrations,
  type AnalyticsIntegrationProvider,
} from '@/components/shared/integrations/EnterpriseAnalyticsIntegrations';
import { GoogleWorkspaceIntegration } from '@/components/shared/integrations/GoogleWorkspaceIntegration';
import { GitHubAstroIntegration } from '@/components/shared/integrations/GitHubAstroIntegration';
import { IntegrationSurfaceProvider } from '@/components/shared/integrations/IntegrationCardShell';
import { OpenAiAdsIntegration } from '@/components/shared/integrations/OpenAiAdsIntegration';
import { SlackAgentIntegration } from '@/components/shared/integrations/SlackAgentIntegration';
import { ShopifyIntegration } from '@/components/shared/integrations/ShopifyIntegration';
import { WebflowIntegration } from '@/components/shared/integrations/WebflowIntegration';
import { WordPressIntegration } from '@/components/shared/integrations/WordPressIntegration';
import { YouTubeIntegration } from '@/components/shared/integrations/YouTubeIntegration';
import { youtubeEnabled } from '@/lib/youtube/config';
import type { YouTubeStatus } from '@/lib/youtube/types';
import { useProject } from '@/contexts/ProjectContext';
import { isAdsModuleEnabledClient } from '@/lib/ads/feature-flags';
import { isAiCmoBetaEnabled } from '@/lib/attune/growth/ai-cmo-feature';
import { isWordPressMcpEnabledClient } from '@/lib/wordpress/feature-flags';
import {
  isGitHubWebsiteEnabledClient,
  isGitHubBindingEnabledClient,
} from '@/lib/github/feature-flags';
import type { GrowthProgramSetupState, GrowthProgramSetupStatus } from '@/types/growth-program';
import {
  CapabilityCatalogItem,
  CapabilityFilter,
  CapabilitySearch,
  CapabilityStatus,
} from './CapabilityCatalog';

export type IntegrationToolId =
  | AnalyticsIntegrationProvider
  | 'openai_ads'
  | 'google_workspace'
  | 'shopify'
  | 'webflow'
  | 'wordpress'
  | 'github'
  | 'attune_mcp'
  | 'custom_mcp'
  | 'slack'
  | 'youtube'
  | 'intelligence';

type IntegrationGroup =
  | 'Intelligence'
  | 'Analytics data'
  | 'Paid media'
  | 'Delivery'
  | 'Background Agent access';
export type IntegrationCatalog = 'all' | 'plugins' | 'mcp';
type CatalogStatusTone = 'live' | 'idle' | 'warning' | 'danger' | 'blocked' | 'loading';

const CMO_AUDIT_IN_PROGRESS_STATUSES = new Set<GrowthProgramSetupStatus>([
  'ready_to_audit',
  'audit_queued',
  'audit_running',
]);

export interface IntegrationTool {
  id: IntegrationToolId;
  group: IntegrationGroup;
  title: string;
  shortTitle: string;
  description: string;
  icon: ReactNode;
  auth: string;
  requiresProject?: boolean;
  requiresWorkspaceExports?: boolean;
  badge?: string;
}

export interface CatalogStatus {
  configured?: boolean;
  label: string;
  tone: CatalogStatusTone;
  detail?: string;
}

interface AnalyticsSourceSummary {
  provider: AnalyticsIntegrationProvider;
  status?: string | null;
  lastSyncStatus?: string | null;
  lastSyncError?: string | null;
}

interface AnalyticsStatusSummary {
  sources?: AnalyticsSourceSummary[];
}

interface WorkspaceStatusSummary {
  connected?: boolean;
  connection?: {
    status?: string | null;
    warnings?: string[] | null;
  } | null;
}

interface ShopifyStatusSummary {
  connected?: boolean;
  configured?: boolean;
  connections?: Array<{ status?: string | null }>;
}

interface WebflowStatusSummary {
  connected?: boolean;
  connection?: { status?: string | null; warnings?: string[] | null } | null;
  sites?: Array<{ project_id?: string; revoked_at?: string | null }>;
  counts?: { sites?: number };
  warning?: string | null;
  syncRuns?: Array<{
    status?: string | null;
    error?: string | null;
    errorMessage?: string | null;
    error_message?: string | null;
  }> | null;
  lastSync?: {
    status?: string | null;
    error?: string | null;
    errorMessage?: string | null;
    error_message?: string | null;
  } | null;
}

interface WordPressStatusSummary {
  enabled?: boolean;
  connected?: boolean;
  binding?: {
    title?: string | null;
    canRead?: boolean;
    canWrite?: boolean;
    statsAvailable?: boolean;
  } | null;
  permissions?: {
    read?: boolean;
    write?: boolean;
    statistics?: boolean;
    disabledReasons?: string[] | null;
  } | null;
  needsAttention?: boolean;
}

interface GitHubStatusSummary {
  installation?: { status?: string | null } | null;
  binding?: {
    status?: string | null;
    repository_full_name?: string | null;
    framework?: 'astro' | 'next' | null;
    detected_astro?: boolean;
  } | null;
}

interface GscStatusSummary {
  connected?: boolean;
  needsReconnect?: boolean;
  properties?: Array<{ projectId?: string | null; lastSyncStatus?: string | null }>;
}

interface AttuneKeysSummary {
  keys?: Array<{ status?: string | null; revoked_at?: string | null }>;
}

interface CustomMcpStatusConnection {
  status?: string | null;
  exposeToMcp?: boolean | null;
  lastSyncStatus?: string | null;
  lastSyncError?: string | null;
  toolCount?: number | null;
  enabledToolCount?: number | null;
}

interface CustomMcpStatusResponse {
  connections?: CustomMcpStatusConnection[];
}

interface SlackStatusResponse {
  connected?: boolean;
  installations?: Array<{
    status?: string | null;
    teamName?: string | null;
    enterpriseName?: string | null;
  }>;
}

const ANALYTICS_TOOL_IDS: AnalyticsIntegrationProvider[] = [
  'ga4',
  'gsc',
  'bing_webmaster',
  'clarity',
  'cloudflare',
];

const INTEGRATION_GROUPS: IntegrationGroup[] = [
  'Intelligence',
  'Analytics data',
  'Paid media',
  'Delivery',
  'Background Agent access',
];

const INTEGRATION_TOOLS: IntegrationTool[] = [
  {
    id: 'youtube',
    group: 'Intelligence',
    title: 'YouTube',
    shortTitle: 'YouTube',
    description: 'Track your videos and Shorts, follow competitors, and connect AI citations.',
    icon: <Youtube size={18} />,
    auth: 'Channel URL',
    requiresProject: true,
  },
  {
    id: 'intelligence',
    group: 'Intelligence',
    title: 'Attune Intelligence',
    shortTitle: 'Intelligence',
    description: 'Track buyer questions across ChatGPT, Gemini and Google AI Overviews.',
    icon: <Search size={18} />,
    auth: 'Prompts',
    requiresProject: true,
  },
  {
    id: 'ga4',
    group: 'Analytics data',
    title: 'Google Analytics',
    shortTitle: 'GA4',
    description: 'GA4 page, channel, conversion, and revenue aggregates.',
    icon: <BarChart3 size={18} />,
    auth: 'Service account',
    requiresProject: true,
  },
  {
    id: 'gsc',
    group: 'Analytics data',
    title: 'Google Search Console',
    shortTitle: 'GSC',
    description: 'Google organic query, page, click, impression, CTR, and position data.',
    icon: <Search size={18} />,
    auth: 'OAuth',
    requiresProject: true,
  },
  {
    id: 'bing_webmaster',
    group: 'Analytics data',
    title: 'Bing Webmaster Tools',
    shortTitle: 'Bing',
    description: 'Bing page and query search performance aggregates.',
    icon: <Search size={18} />,
    auth: 'API key',
    requiresProject: true,
  },
  {
    id: 'clarity',
    group: 'Analytics data',
    title: 'Microsoft Clarity',
    shortTitle: 'Clarity',
    description: 'UX friction snapshots from the Clarity Data Export API.',
    icon: <MousePointerClick size={18} />,
    auth: 'Export token',
    requiresProject: true,
  },
  {
    id: 'cloudflare',
    group: 'Analytics data',
    title: 'Cloudflare',
    shortTitle: 'Cloudflare',
    description: 'Web Analytics, Core Web Vitals, and AI crawler activity.',
    icon: <Cloud size={18} />,
    auth: 'API token',
    requiresProject: true,
  },
  {
    id: 'openai_ads',
    group: 'Paid media',
    title: 'ChatGPT Ads',
    shortTitle: 'ChatGPT/OpenAI',
    description: 'Paid visibility in ChatGPT moments through OpenAI Ads.',
    icon: <Megaphone size={18} />,
    auth: 'API key',
    requiresProject: true,
  },
  {
    id: 'shopify',
    group: 'Delivery',
    title: 'Shopify',
    shortTitle: 'Shopify',
    description: 'Product catalog, page/blog copy, metafields, analytics, and approved AI updates.',
    icon: <ShoppingBag size={18} />,
    auth: 'OAuth',
    requiresProject: true,
  },
  {
    id: 'google_workspace',
    group: 'Delivery',
    title: 'Google Workspace',
    shortTitle: 'Workspace',
    description: 'Export delivery to Docs and Sheets.',
    icon: <FileText size={18} />,
    auth: 'OAuth',
    requiresProject: true,
    requiresWorkspaceExports: true,
  },
  {
    id: 'webflow',
    group: 'Delivery',
    title: 'Webflow Pages & CMS',
    shortTitle: 'Webflow',
    description: 'Page mapping, copy drafts, CMS drafts, and publish controls.',
    icon: <Globe size={18} />,
    auth: 'OAuth',
    requiresProject: true,
  },
  {
    id: 'wordpress',
    group: 'Delivery',
    title: 'WordPress.com MCP',
    shortTitle: 'WordPress.com',
    description: 'Approval-gated page and post delivery with native live site statistics.',
    icon: <Globe size={18} />,
    auth: 'OAuth 2.1',
    requiresProject: true,
    badge: 'Official Attune plugin',
  },
  {
    id: 'github',
    group: 'Delivery',
    title: 'Website via GitHub',
    shortTitle: 'Website',
    description: 'Astro and Next.js content access and manager-approved pull request delivery.',
    icon: <GitBranch size={18} />,
    auth: 'GitHub App',
    requiresProject: true,
    badge: 'Official Attune support',
  },
  {
    id: 'slack',
    group: 'Background Agent access',
    title: 'Attune for Slack',
    shortTitle: 'Slack',
    description: 'Agent View, native streaming, files, approvals, and shared Attune Threads.',
    icon: <Bot size={18} />,
    auth: 'OAuth',
    badge: 'Agent View',
  },
  {
    id: 'attune_mcp',
    group: 'Background Agent access',
    title: 'Attune MCP',
    shortTitle: 'MCP',
    description: 'Hosted MCP endpoint and user-scoped API keys for background agent clients.',
    icon: <Bot size={18} />,
    auth: 'API key',
  },
  {
    id: 'custom_mcp',
    group: 'Background Agent access',
    title: 'Custom MCP',
    shortTitle: 'Custom MCP',
    description: 'HTTP MCP servers proxied through Attune background agent access.',
    icon: <Server size={18} />,
    auth: 'HTTP/OAuth',
    requiresProject: true,
  },
];

const STATUS_META: Record<CatalogStatusTone, { dot: string }> = {
  live: { dot: 'status.success.fg' },
  idle: { dot: 'ui.text.tertiary' },
  warning: { dot: 'status.warning.fg' },
  danger: { dot: 'status.danger.fg' },
  blocked: { dot: 'status.warning.fg' },
  loading: { dot: 'accent.solid' },
};

export function getIntegrationCatalogTools(
  options: Parameters<typeof getWorkspaceModuleCatalog>[0] = {}
): IntegrationTool[] {
  const projectTools = getWorkspaceModuleCatalog(options).flatMap(module => {
    const tool = INTEGRATION_TOOLS.find(item => item.id === module.id);
    return tool ? [{ ...tool, title: module.name, description: module.description }] : [];
  });
  // Slack connects at workspace scope, outside the project setup catalog.
  return [...projectTools, ...INTEGRATION_TOOLS.filter(tool => tool.id === 'slack')];
}

/** Shared catalog surface; it owns no authentication, provider requests or setup dialogs. */
export function IntegrationModuleCatalogView({
  tools,
  statuses = {},
  hasProject = true,
  onboarding = false,
  onOpen,
}: {
  tools: IntegrationTool[];
  statuses?: Partial<Record<IntegrationToolId, CatalogStatus>>;
  hasProject?: boolean;
  onboarding?: boolean;
  onOpen: (id: IntegrationToolId) => void;
}) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'connected' | 'attention'>('all');
  const terms = search.toLowerCase().trim().split(/\s+/).filter(Boolean);
  const filtered = tools.filter(tool => {
    const status = statuses[tool.id];
    if (filter === 'connected' && status?.tone !== 'live' && !status?.configured) return false;
    if (filter === 'attention' && !['warning', 'danger', 'blocked'].includes(status?.tone || ''))
      return false;
    const text = [tool.title, tool.shortTitle, tool.description, tool.group]
      .join(' ')
      .toLowerCase();
    return terms.every(term => text.includes(term));
  });
  const recommended = RECOMMENDED_WORKSPACE_MODULE_IDS as readonly string[];
  const sections =
    onboarding && !terms.length
      ? [
          { title: 'Recommended', tools: filtered.filter(tool => recommended.includes(tool.id)) },
          { title: 'More modules', tools: filtered.filter(tool => !recommended.includes(tool.id)) },
        ]
      : onboarding
        ? [{ title: 'All modules', tools: filtered }]
        : INTEGRATION_GROUPS.map(group => ({
            title: group,
            tools: filtered.filter(tool => tool.group === group),
          }));
  return (
    <VStack align="stretch" gap="5">
      <Flex
        gap="3"
        justify="space-between"
        align={{ base: 'stretch', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
      >
        <CapabilitySearch
          label={onboarding ? 'Search all modules' : 'Search plugins'}
          value={search}
          onChange={setSearch}
        />
        {!onboarding ? (
          <HStack gap="4" overflowX="auto">
            <CapabilityFilter active={filter === 'all'} onClick={() => setFilter('all')}>
              All {tools.length}
            </CapabilityFilter>
            <CapabilityFilter
              active={filter === 'connected'}
              onClick={() => setFilter('connected')}
            >
              Connected{' '}
              {
                tools.filter(
                  tool => statuses[tool.id]?.tone === 'live' || statuses[tool.id]?.configured
                ).length
              }
            </CapabilityFilter>
            <CapabilityFilter
              active={filter === 'attention'}
              onClick={() => setFilter('attention')}
            >
              Needs attention{' '}
              {
                tools.filter(tool =>
                  ['warning', 'danger', 'blocked'].includes(statuses[tool.id]?.tone || '')
                ).length
              }
            </CapabilityFilter>
          </HStack>
        ) : null}
      </Flex>
      {!filtered.length ? (
        <Box textAlign="center" py="8">
          <Text fontSize="sm" color="ui.text.secondary">
            No {onboarding ? 'modules' : 'plugins'} found.
          </Text>
          <Button
            variant="ghost"
            size="xs"
            mt="2"
            onClick={() => {
              setSearch('');
              setFilter('all');
            }}
          >
            Clear search
          </Button>
        </Box>
      ) : null}
      {sections
        .filter(section => section.tools.length)
        .map(section => (
          <Box key={section.title}>
            <Text fontSize="sm" fontWeight="600" color="ui.text.primary" mb="3">
              {section.title}
            </Text>
            <SimpleGrid columns={{ base: 1, lg: 2 }} gap="2.5">
              {section.tools.map(tool => (
                <IntegrationToolRow
                  key={tool.id}
                  tool={tool}
                  status={statuses[tool.id] || defaultStatus(tool, hasProject)}
                  projectMissing={Boolean(tool.requiresProject && !hasProject)}
                  onOpen={() => onOpen(tool.id)}
                />
              ))}
            </SimpleGrid>
          </Box>
        ))}
    </VStack>
  );
}

function isAnalyticsTool(id: IntegrationToolId): id is AnalyticsIntegrationProvider {
  return ANALYTICS_TOOL_IDS.includes(id as AnalyticsIntegrationProvider);
}

function integrationToolIdFromSearch(search: string): IntegrationToolId | null {
  const params = new URLSearchParams(search);
  const requested = params.get('setup_module') || params.get('integration');
  if (INTEGRATION_TOOLS.some(tool => tool.id === requested)) {
    return requested as IntegrationToolId;
  }

  const keys = Array.from(params.keys());
  if (keys.some(key => key.startsWith('gsc_'))) return 'gsc';
  if (keys.some(key => key.startsWith('workspace_'))) return 'google_workspace';
  if (keys.some(key => key.startsWith('shopify_'))) return 'shopify';
  if (keys.some(key => key.startsWith('webflow_'))) return 'webflow';
  if (keys.some(key => key.startsWith('wordpress_'))) return 'wordpress';
  if (keys.some(key => key.startsWith('github_'))) return 'github';
  return null;
}

function defaultStatus(tool: IntegrationTool, hasProject: boolean): CatalogStatus {
  if (tool.requiresProject && !hasProject) {
    return { label: 'Project required', tone: 'blocked' };
  }
  return { label: tool.id === 'intelligence' ? 'Not configured' : 'Not connected', tone: 'idle' };
}

function sourceStatus(sources: AnalyticsSourceSummary[]): CatalogStatus {
  if (sources.length === 0) return { label: 'Not connected', tone: 'idle' };
  const failed = sources.some(
    source =>
      source.status === 'error' ||
      source.lastSyncStatus === 'failed' ||
      Boolean(source.lastSyncError)
  );
  if (failed) {
    return {
      label: 'Needs attention',
      tone: 'danger',
      configured: true,
      detail: sources.length === 1 ? '1 source' : `${sources.length} sources`,
    };
  }
  const notReady = sources.some(source => source.status !== 'connected');
  if (notReady) {
    return {
      label: 'Needs attention',
      tone: 'warning',
      configured: true,
      detail: sources.length === 1 ? '1 source' : `${sources.length} sources`,
    };
  }
  return {
    label: 'Live',
    tone: 'live',
    configured: true,
    detail: sources.length === 1 ? '1 source' : `${sources.length} sources`,
  };
}

function connectedStatus(connected: boolean, warning = false, detail?: string): CatalogStatus {
  if (!connected) return { label: 'Not connected', tone: 'idle', detail };
  if (warning) return { label: 'Needs attention', tone: 'warning', detail, configured: true };
  return { label: 'Live', tone: 'live', detail };
}

/** Account authorization is not a project resource binding. Merge the two GSC sources deterministically. */
export function gscCatalogStatus(
  projectId: string,
  oauth?: GscStatusSummary,
  analytics?: CatalogStatus
): CatalogStatus {
  const properties = (oauth?.properties || []).filter(property => property.projectId === projectId);
  if (analytics?.configured || analytics?.tone === 'live') {
    if (analytics.tone === 'danger') return analytics;
    return oauth?.needsReconnect
      ? { ...analytics, label: 'Reconnect', tone: 'warning', configured: true }
      : analytics;
  }
  if (properties.length) {
    const warning =
      !oauth?.connected ||
      oauth.needsReconnect ||
      properties.some(property => property.lastSyncStatus === 'failed');
    return {
      label: warning ? 'Needs attention' : 'Live',
      tone: warning ? 'warning' : 'live',
      configured: true,
      detail: `${properties.length} ${properties.length === 1 ? 'property' : 'properties'}`,
    };
  }
  if (oauth?.connected) return { label: 'Select property', tone: 'warning', configured: false };
  if (oauth?.needsReconnect) return { label: 'Reconnect', tone: 'warning', configured: false };
  return (
    analytics ||
    (oauth
      ? { label: 'Not connected', tone: 'idle', configured: false }
      : { label: 'Status unavailable', tone: 'warning', configured: false })
  );
}

export function webflowCatalogStatus(
  projectId: string,
  status: WebflowStatusSummary
): CatalogStatus {
  const connected = Boolean(status.connected || status.connection);
  if (!connected) return { label: 'Not connected', tone: 'idle', configured: false };
  const siteCount = status.sites
    ? status.sites.filter(
        site => !site.revoked_at && (!site.project_id || site.project_id === projectId)
      ).length
    : status.counts?.sites || 0;
  if (!siteCount) return { label: 'Select site', tone: 'warning', configured: false };
  const latestSync = status.lastSync || status.syncRuns?.[0];
  const failedSync =
    latestSync?.status?.toLowerCase() === 'failed' ||
    latestSync?.error ||
    latestSync?.errorMessage ||
    latestSync?.error_message;
  const warning = Boolean(
    status.warning ||
      status.connection?.status === 'warning' ||
      status.connection?.warnings?.length ||
      failedSync
  );
  return {
    label: warning
      ? 'Needs attention'
      : status.connection?.status === 'syncing'
        ? 'Syncing'
        : 'Live',
    tone: warning ? 'warning' : status.connection?.status === 'syncing' ? 'loading' : 'live',
    configured: true,
    detail: `${siteCount} ${siteCount === 1 ? 'site' : 'sites'}`,
  };
}

/** Publish each independent group as it settles; a slow provider never holds the other rows. */
export async function loadIntegrationCatalogStatuses({
  projectId,
  workspaceId,
  brandDomain,
  toolIds,
  read,
  onSettled,
  isCurrent,
}: {
  projectId?: string | null;
  workspaceId?: string | null;
  brandDomain?: string | null;
  toolIds: IntegrationToolId[];
  read: <T>(url: string) => Promise<T>;
  onSettled: (statuses: Partial<Record<IntegrationToolId, CatalogStatus>>) => void;
  isCurrent: () => boolean;
}): Promise<void> {
  const groups: Promise<unknown>[] = [];
  const includes = (id: IntegrationToolId) => toolIds.includes(id);
  const publish = (statuses: Partial<Record<IntegrationToolId, CatalogStatus>>) => {
    if (isCurrent()) onSettled(statuses);
  };
  const unavailable = { label: 'Status unavailable', tone: 'warning' as const };
  function group<T>(id: IntegrationToolId, url: string, resolve: (value: T) => CatalogStatus) {
    if (!includes(id)) return;
    groups.push(
      read<T>(url)
        .then(value => publish({ [id]: resolve(value) }))
        .catch(() => publish({ [id]: unavailable }))
    );
  }
  if (projectId) {
    const query = `?projectId=${encodeURIComponent(projectId)}`;
    group<YouTubeStatus>('youtube', `/api/integrations/youtube${query}`, youtube => ({
      label: youtube.connected
        ? ['queued', 'running'].includes(youtube.status)
          ? 'Updating'
          : youtube.error
            ? 'Needs attention'
            : 'Daily updates'
        : youtube.configured
          ? 'Paused'
          : 'Not connected',
      tone: youtube.connected ? (youtube.error ? 'warning' : 'live') : 'idle',
      configured: youtube.connected,
      detail: youtube.config.channels.length
        ? `${youtube.config.channels.length} channels`
        : undefined,
    }));
    group<IntelligenceStatusResponse>(
      'intelligence',
      `/api/projects/${encodeURIComponent(projectId)}/intelligence/status`,
      intelligence =>
        intelligence.collectionManaged
          ? { label: 'Managed by Attune', tone: 'idle', configured: true }
          : intelligence.status === 'ready'
            ? { label: 'Ready', tone: 'live', configured: true }
            : intelligence.status === 'collecting'
              ? { label: 'Collecting', tone: 'loading', configured: true }
              : intelligence.status === 'failed'
                ? { label: 'Needs attention', tone: 'danger', configured: true }
                : { label: 'Not configured', tone: 'idle' }
    );

    let oauthGsc: GscStatusSummary | undefined;
    let analyticsGsc: CatalogStatus | undefined;
    const publishGsc = () => {
      if (includes('gsc')) publish({ gsc: gscCatalogStatus(projectId, oauthGsc, analyticsGsc) });
    };
    const analyticsIds = ANALYTICS_TOOL_IDS.filter(includes);
    if (analyticsIds.length) {
      groups.push(
        read<AnalyticsStatusSummary>(`/api/integrations/analytics/status${query}`)
          .then(analytics => {
            const statuses: Partial<Record<IntegrationToolId, CatalogStatus>> = {};
            for (const provider of analyticsIds) {
              const status = sourceStatus(
                (analytics.sources || []).filter(source => source.provider === provider)
              );
              if (provider === 'gsc') analyticsGsc = status;
              else statuses[provider] = status;
            }
            publish(statuses);
            publishGsc();
          })
          .catch(() => {
            analyticsGsc = unavailable;
            publish(
              Object.fromEntries(
                analyticsIds.filter(id => id !== 'gsc').map(id => [id, unavailable])
              )
            );
            publishGsc();
          })
      );
    }
    if (includes('gsc')) {
      const gscQuery = new URLSearchParams({
        projectId,
        ...(brandDomain ? { brand_domain: brandDomain } : {}),
      });
      groups.push(
        read<GscStatusSummary>(`/api/integrations/gsc/status?${gscQuery}`)
          .then(oauth => {
            oauthGsc = oauth;
            publishGsc();
          })
          .catch(publishGsc)
      );
    }
    group<AnalyticsStatusSummary>('openai_ads', `/api/integrations/ads/status${query}`, ads =>
      sourceStatus(ads.sources || [])
    );
    group<WorkspaceStatusSummary>(
      'google_workspace',
      '/api/integrations/google-workspace/status',
      workspace =>
        connectedStatus(
          Boolean(workspace.connected),
          Boolean(workspace.connection?.warnings?.length)
        )
    );
    group<ShopifyStatusSummary>('shopify', `/api/integrations/shopify/status${query}`, shopify =>
      shopify.configured === false
        ? { label: 'Needs setup', tone: 'warning' }
        : connectedStatus(Boolean(shopify.connected))
    );
    group<WebflowStatusSummary>('webflow', `/api/integrations/webflow/status${query}`, webflow =>
      webflowCatalogStatus(projectId, webflow)
    );
    group<WordPressStatusSummary>(
      'wordpress',
      `/api/integrations/wordpress/status${query}`,
      wordpress => {
        const permissions = wordpress.permissions || {};
        const binding = wordpress.binding || null;
        if (wordpress.connected && !binding)
          return { label: 'Select site', tone: 'warning', configured: false };
        const writeReady = Boolean(binding?.canWrite ?? permissions.write);
        const readReady = Boolean(binding?.canRead ?? permissions.read);
        return connectedStatus(
          Boolean(wordpress.connected),
          Boolean(wordpress.needsAttention || (wordpress.connected && !writeReady)),
          wordpress.connected
            ? binding?.title ||
                (writeReady ? 'Write ready' : readReady ? 'Read only' : 'Select site')
            : undefined
        );
      }
    );
    group<GitHubStatusSummary>('github', `/api/integrations/github/status${query}`, github => {
      if (github.binding && !isGitHubBindingEnabledClient(github.binding))
        return { label: 'Unavailable', tone: 'blocked', configured: false };
      return connectedStatus(
        Boolean(github.binding),
        Boolean(
          github.binding &&
            (github.binding.status !== 'active' ||
              (github.installation?.status && github.installation.status !== 'connected'))
        ),
        github.binding?.repository_full_name || undefined
      );
    });
    group<CustomMcpStatusResponse>(
      'custom_mcp',
      `/api/integrations/custom-mcp/status${query}`,
      customMcpStatus
    );
  }
  group<AttuneKeysSummary>('attune_mcp', '/api/attune/auth/keys', keys => {
    const activeKeys = (keys.keys || []).filter(key => key.status !== 'revoked' && !key.revoked_at);
    return connectedStatus(
      activeKeys.length > 0,
      false,
      `${activeKeys.length} ${activeKeys.length === 1 ? 'active key' : 'active keys'}`
    );
  });
  if (workspaceId) {
    group<SlackStatusResponse>(
      'slack',
      `/api/integrations/slack/status?workspaceId=${encodeURIComponent(workspaceId)}`,
      slack => {
        const active = (slack.installations || []).filter(item => item.status === 'active');
        return connectedStatus(
          Boolean(slack.connected),
          false,
          active[0]?.enterpriseName || active[0]?.teamName || undefined
        );
      }
    );
  }
  if (!workspaceId && includes('slack')) {
    publish({ slack: { label: 'Choose workspace', tone: 'idle' } });
  }
  await Promise.allSettled(groups);
}

function customMcpStatus(status: CustomMcpStatusResponse): CatalogStatus {
  const connections = status.connections || [];
  if (connections.length === 0) return { label: 'Not connected', tone: 'idle' };

  const hasError = connections.some(connection => {
    const connectionStatus = (connection.status || '').toLowerCase();
    const lastSyncStatus = (connection.lastSyncStatus || '').toLowerCase();
    return (
      connectionStatus.includes('error') ||
      connectionStatus.includes('fail') ||
      lastSyncStatus.includes('error') ||
      lastSyncStatus.includes('fail') ||
      Boolean(connection.lastSyncError)
    );
  });
  if (hasError) return { label: 'Needs attention', tone: 'danger', configured: true };

  const exposedConnections = connections.filter(connection => connection.exposeToMcp !== false);
  const enabledToolCount = connections.reduce(
    (sum, connection) => sum + Math.max(0, connection.enabledToolCount || 0),
    0
  );
  const toolCount = connections.reduce(
    (sum, connection) => sum + Math.max(0, connection.toolCount || 0),
    0
  );

  if (enabledToolCount > 0 && exposedConnections.length > 0) {
    return {
      label: 'Live',
      tone: 'live',
      detail: `${exposedConnections.length} connections / ${enabledToolCount} tools`,
    };
  }

  return {
    label: 'Connected',
    tone: 'warning',
    detail: `${connections.length} connections / ${toolCount || 0} tools`,
  };
}

async function readJson<T>(url: string, token: string): Promise<T> {
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(`Failed to load ${url}`);
  return response.json() as Promise<T>;
}

export function IntegrationsSettingsPanel({
  hasProject,
  canManage,
  workspaceExportsEnabled,
  showHeader = true,
  catalog = 'all',
  showGrowthProgramSetup = true,
  onboarding = false,
  activeModuleId,
  onActiveModuleChange,
  returnTo,
  onConfigured,
  onConfiguredCountChange,
}: {
  hasProject: boolean;
  canManage: boolean;
  workspaceExportsEnabled: boolean;
  showHeader?: boolean;
  catalog?: IntegrationCatalog;
  showGrowthProgramSetup?: boolean;
  onboarding?: boolean;
  activeModuleId?: string | null;
  onActiveModuleChange?: (id: string | null) => void;
  returnTo?: string;
  onConfigured?: (id: string) => void;
  onConfiguredCountChange?: (count: number) => void;
}) {
  const { user } = useAuth();
  const { currentProject, updateProject } = useProject();
  const aiCmoBetaEnabled = isAiCmoBetaEnabled(currentProject);
  const showCmoSetup = showGrowthProgramSetup && aiCmoBetaEnabled;
  const adsModuleEnabled = isAdsModuleEnabledClient();
  const wordpressEnabled = isWordPressMcpEnabledClient();
  const githubEnabled = isGitHubWebsiteEnabledClient();
  const visibleTools = useMemo(
    () =>
      getIntegrationCatalogTools({
        projectId: currentProject?.id,
        adsEnabled: adsModuleEnabled,
        wordpressEnabled,
        githubEnabled,
        workspaceExportsEnabled,
      }).filter(tool => {
        if (tool.id === 'openai_ads' && !adsModuleEnabled) return false;
        if (tool.id === 'wordpress' && !wordpressEnabled) return false;
        if (tool.id === 'github' && !githubEnabled) return false;
        if (tool.id === 'youtube' && !youtubeEnabled()) return false;
        const isMcpTool = tool.id === 'attune_mcp' || tool.id === 'custom_mcp';
        if (catalog === 'plugins' && isMcpTool) return false;
        if (catalog === 'mcp' && !isMcpTool) return false;
        return !tool.requiresWorkspaceExports || workspaceExportsEnabled;
      }),
    [
      adsModuleEnabled,
      catalog,
      currentProject?.id,
      githubEnabled,
      wordpressEnabled,
      workspaceExportsEnabled,
    ]
  );
  const [localActiveToolId, setLocalActiveToolId] = useState<IntegrationToolId | null>(null);
  const activeToolId = (
    activeModuleId === undefined ? localActiveToolId : activeModuleId
  ) as IntegrationToolId | null;
  const activeToolRef = useRef(activeToolId);
  activeToolRef.current = activeToolId;
  const activeGenerationRef = useRef(0);
  const projectScopeRef = useRef(currentProject?.id);
  projectScopeRef.current = currentProject?.id;
  const onActiveModuleChangeRef = useRef(onActiveModuleChange);
  onActiveModuleChangeRef.current = onActiveModuleChange;
  const focusRef = useRef<HTMLElement | null>(null);
  const setActiveToolId = (id: IntegrationToolId | null) => {
    if (id === 'intelligence' && currentProject?.intelligence_collection_linked) {
      window.location.assign('/app/settings?tab=prompts');
      return;
    }
    activeGenerationRef.current += 1;
    activeToolRef.current = id;
    if (id && typeof document !== 'undefined')
      focusRef.current = document.activeElement as HTMLElement;
    setLocalActiveToolId(id);
    onActiveModuleChangeRef.current?.(id);
  };
  useEffect(() => {
    if (activeToolId === 'intelligence' && currentProject?.intelligence_collection_linked) {
      window.location.replace('/app/settings?tab=prompts');
    }
  }, [activeToolId, currentProject?.intelligence_collection_linked]);
  const onConfiguredRef = useRef(onConfigured);
  onConfiguredRef.current = onConfigured;
  const onConfiguredCountRef = useRef(onConfiguredCountChange);
  onConfiguredCountRef.current = onConfiguredCountChange;
  const [toolStatuses, setToolStatuses] = useState<
    Partial<Record<IntegrationToolId, CatalogStatus>>
  >({});
  const [statusRefreshToken, setStatusRefreshToken] = useState(0);
  const [updatingAiCmoBeta, setUpdatingAiCmoBeta] = useState(false);
  const [aiCmoBetaError, setAiCmoBetaError] = useState<string | null>(null);
  const [startingCmoAudit, setStartingCmoAudit] = useState(false);
  const [cmoSetupPollGeneration, setCmoSetupPollGeneration] = useState(0);
  const [cmoSetupSnapshot, setCmoSetupSnapshot] = useState<{
    projectId: string;
    setup: GrowthProgramSetupState;
  } | null>(null);
  const [cmoSetupError, setCmoSetupError] = useState<string | null>(null);
  const [cmoSetupErrorCode, setCmoSetupErrorCode] = useState<string | null>(null);
  const currentCmoSetup =
    currentProject?.id && cmoSetupSnapshot?.projectId === currentProject.id
      ? cmoSetupSnapshot.setup
      : null;
  const cmoSetupStatus = currentCmoSetup?.status || null;
  const cmoAuditStarted = Boolean(
    cmoSetupStatus && CMO_AUDIT_IN_PROGRESS_STATUSES.has(cmoSetupStatus)
  );
  const cmoAuditReady = cmoSetupStatus === 'proposal_ready';
  const cmoSetupActive = cmoSetupStatus === 'active' || cmoSetupStatus === 'activating';
  const cmoAuditFailed = cmoSetupStatus === 'failed';
  const cmoAuditThreadUrl = currentCmoSetup?.audit_thread_url || null;

  const brandDomain = useMemo(() => {
    const settings = currentProject?.brand_settings;
    if (settings?.website_domain) return settings.website_domain;
    if (!settings?.website_url) return null;
    try {
      return new URL(settings.website_url).hostname.replace(/^www\./, '');
    } catch {
      return null;
    }
  }, [currentProject?.brand_settings]);

  useEffect(() => {
    if (activeModuleId !== undefined) return;
    const requested = integrationToolIdFromSearch(window.location.search);
    if (requested && visibleTools.some(tool => tool.id === requested)) {
      setActiveToolId(requested);
    }
  }, [visibleTools]);

  useEffect(() => {
    if (activeToolId && !visibleTools.some(tool => tool.id === activeToolId)) {
      setActiveToolId(null);
    }
  }, [activeToolId, visibleTools]);

  useEffect(() => {
    if (!showCmoSetup || !user || !currentProject?.id) return;

    const projectId = currentProject.id;
    const controller = new AbortController();
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const schedule = (delayMs: number) => {
      if (stopped) return;
      timer = setTimeout(() => void loadSetupStatus(), delayMs);
    };

    const loadSetupStatus = async () => {
      try {
        const token = await user.getIdToken();
        const response = await fetch('/api/attune/growth-programs/setup-status', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
          body: JSON.stringify({ projectId }),
        });
        const payload = (await response.json().catch(() => ({}))) as {
          setup?: GrowthProgramSetupState;
        };
        if (stopped) return;
        if (!response.ok || !payload.setup) {
          schedule(5_000);
          return;
        }
        setCmoSetupSnapshot({ projectId, setup: payload.setup });
        if (payload.setup.status === 'proposal_ready') {
          window.sessionStorage.removeItem(`attune-ai-cmo-setup:${projectId}`);
          return;
        }
        if (CMO_AUDIT_IN_PROGRESS_STATUSES.has(payload.setup.status)) {
          schedule(3_000);
        }
      } catch (error) {
        if (!stopped && !(error instanceof DOMException && error.name === 'AbortError')) {
          schedule(5_000);
        }
      }
    };

    void loadSetupStatus();
    return () => {
      stopped = true;
      controller.abort();
      if (timer) clearTimeout(timer);
    };
  }, [cmoSetupPollGeneration, currentProject?.id, showCmoSetup, user]);

  useEffect(() => {
    let cancelled = false;
    const baseStatuses = Object.fromEntries(
      visibleTools.map(tool => [
        tool.id,
        user && !(tool.requiresProject && !hasProject)
          ? { label: 'Checking', tone: 'loading' as const }
          : defaultStatus(tool, hasProject),
      ])
    ) as Partial<Record<IntegrationToolId, CatalogStatus>>;

    setToolStatuses(baseStatuses);

    async function loadStatuses() {
      if (!user) return;
      let token: string;
      try {
        token = await user.getIdToken();
      } catch {
        if (!cancelled) {
          setToolStatuses(
            Object.fromEntries(
              visibleTools.map(tool => [
                tool.id,
                tool.requiresProject && !hasProject
                  ? defaultStatus(tool, hasProject)
                  : { label: 'Status unavailable', tone: 'warning' as const },
              ])
            ) as Partial<Record<IntegrationToolId, CatalogStatus>>
          );
        }
        return;
      }
      const projectId = currentProject?.id;
      await loadIntegrationCatalogStatuses({
        projectId: hasProject ? projectId : null,
        workspaceId: currentProject?.workspace_id,
        brandDomain,
        toolIds: visibleTools.map(tool => tool.id),
        read: url => readJson(url, token),
        isCurrent: () => !cancelled && projectScopeRef.current === projectId,
        onSettled: statuses => setToolStatuses(previous => ({ ...previous, ...statuses })),
      });
    }

    void loadStatuses();
    return () => {
      cancelled = true;
    };
  }, [
    brandDomain,
    currentProject?.id,
    currentProject?.workspace_id,
    hasProject,
    statusRefreshToken,
    user,
    visibleTools,
  ]);

  const configuredCount = visibleTools.filter(
    tool => toolStatuses[tool.id]?.tone === 'live' || toolStatuses[tool.id]?.configured
  ).length;
  useEffect(() => {
    onConfiguredCountRef.current?.(configuredCount);
  }, [configuredCount]);

  const activeTool = activeToolId
    ? visibleTools.find(tool => tool.id === activeToolId) || null
    : null;
  const completeActiveModule = useMemo(() => {
    const id = activeTool?.id;
    const generation = activeGenerationRef.current;
    const projectId = currentProject?.id;
    return () => {
      if (!id || projectScopeRef.current !== projectId) return;
      setToolStatuses(value => ({
        ...value,
        [id]: {
          label: id === 'intelligence' ? 'Collecting' : 'Connected',
          tone: id === 'intelligence' ? 'loading' : 'live',
          configured: true,
        },
      }));
      onConfiguredRef.current?.(id);
      setStatusRefreshToken(token => token + 1);
      if (activeToolRef.current === id && activeGenerationRef.current === generation)
        setActiveToolId(null);
    };
  }, [activeTool?.id, currentProject?.id]);
  const cmoSourceIds: IntegrationToolId[] = [
    'gsc',
    'ga4',
    'bing_webmaster',
    'clarity',
    'cloudflare',
    'openai_ads',
    'shopify',
    'webflow',
    'wordpress',
    'custom_mcp',
  ];
  const connectedCmoSourceIds = cmoSourceIds.filter(id => toolStatuses[id]?.tone === 'live');
  const missingPrimarySourceIds = (['gsc', 'ga4'] as IntegrationToolId[]).filter(
    id => toolStatuses[id]?.tone !== 'live'
  );
  const cmoStatusesLoading = (['gsc', 'ga4'] as IntegrationToolId[]).some(
    id => !toolStatuses[id] || toolStatuses[id]?.tone === 'loading'
  );

  const startCmoAudit = async () => {
    if (!user || !currentProject?.id || startingCmoAudit) return;
    const projectId = currentProject.id;
    setStartingCmoAudit(true);
    setCmoSetupError(null);
    setCmoSetupErrorCode(null);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/attune/growth-programs/setup-start', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projectId }),
      });
      const payload = (await response.json().catch(() => ({}))) as {
        error?: string;
        code?: string;
        setup?: GrowthProgramSetupState;
      };
      if (!response.ok) {
        setCmoSetupErrorCode(payload.code || null);
        throw new Error(payload.error || 'Unable to start the Growth Plan audit');
      }
      window.sessionStorage.removeItem(`attune-ai-cmo-setup:${projectId}`);
      if (payload.setup) setCmoSetupSnapshot({ projectId, setup: payload.setup });
      setCmoSetupPollGeneration(generation => generation + 1);
    } catch (error) {
      setCmoSetupError(
        error instanceof Error ? error.message : 'Unable to start the Growth Plan audit'
      );
    } finally {
      setStartingCmoAudit(false);
    }
  };

  const setAiCmoBetaEnabled = async (enabled: boolean) => {
    if (!currentProject?.id || !canManage || updatingAiCmoBeta) return;
    setUpdatingAiCmoBeta(true);
    setAiCmoBetaError(null);
    try {
      await updateProject(currentProject.id, {
        feature_settings: { ai_cmo_beta_enabled: enabled },
      });
      if (!enabled) {
        window.sessionStorage.removeItem(`attune-ai-cmo-setup:${currentProject.id}`);
        setCmoSetupSnapshot(null);
        setCmoSetupError(null);
        setCmoSetupErrorCode(null);
      }
    } catch (error) {
      setAiCmoBetaError(
        error instanceof Error ? error.message : 'Unable to update the AI CMO beta'
      );
    } finally {
      setUpdatingAiCmoBeta(false);
    }
  };

  const cmoSetupStep = cmoSetupActive
    ? 4
    : cmoAuditReady
      ? 3
      : cmoAuditStarted || cmoAuditFailed
        ? 2
        : 1;
  const cmoSetupTitle = cmoSetupActive
    ? 'Your AI CMO is active'
    : cmoAuditReady
      ? 'Your Growth Plan is ready'
      : cmoAuditStarted
        ? 'Attune is auditing your growth system'
        : cmoAuditFailed
          ? 'The setup audit needs attention'
          : 'Connect the data you want Attune to audit';
  const cmoSetupDescription = cmoSetupActive
    ? 'Open the Growth Plan to review progress, decisions, and active work.'
    : cmoAuditReady
      ? 'The audit is complete. Review and approve the Growth Plan before the AI CMO is enabled.'
      : cmoAuditStarted
        ? 'The audit is running in the background. You can keep reviewing or connecting plugins while Attune prepares your Growth Plan.'
        : cmoAuditFailed
          ? currentCmoSetup?.error || 'The audit stopped before creating a Growth Plan. Retry it.'
          : 'Google Search Console and GA4 give the strongest baseline. Add Ads, your CMS, or custom sources when relevant. You can continue with current data; the program will state every evidence gap.';
  const displayedCmoSetupError = cmoSetupError || null;

  return (
    <VStack align="stretch" gap="3">
      {showHeader ? (
        <Box minW={0}>
          <Heading as="h1" fontSize="lg" fontWeight="650" color="ui.text.primary" mb="0.5">
            {catalog === 'mcp' ? 'MCP' : 'Plugins'}
          </Heading>
          <Text fontSize="sm" color="ui.text.secondary">
            {catalog === 'mcp'
              ? 'Manage Attune access keys and external MCP servers for background Agents.'
              : 'Connect and manage the plugins that feed Attune data, exports, and delivery.'}
          </Text>
        </Box>
      ) : null}

      <IntegrationModuleCatalogView
        tools={visibleTools}
        statuses={toolStatuses}
        hasProject={hasProject}
        onboarding={onboarding}
        onOpen={setActiveToolId}
      />

      {showGrowthProgramSetup || showCmoSetup ? (
        <Collapsible.Root mt="2" borderTopWidth="1px" borderColor="ui.border.subtle">
          <Collapsible.Trigger asChild>
            <Button
              variant="ghost"
              w="full"
              justifyContent="space-between"
              px="0"
              py="4"
              h="auto"
              color="ui.text.secondary"
              fontWeight="500"
              _hover={{ color: 'ui.text.primary' }}
            >
              <HStack gap="2">
                <Bot size={15} />
                <Text as="span" fontSize="sm">
                  AI CMO & growth setup
                </Text>
              </HStack>
              <ChevronDown size={14} />
            </Button>
          </Collapsible.Trigger>
          <Collapsible.Content>
            <VStack align="stretch" gap="3" pb="3">
              {showGrowthProgramSetup ? (
                <Box
                  borderWidth="1px"
                  borderColor={aiCmoBetaEnabled ? 'accent.muted' : 'ui.border.subtle'}
                  borderRadius="8px"
                  bg={aiCmoBetaEnabled ? 'ui.surface.selected' : 'ui.surface.card'}
                  px={{ base: '4', md: '5' }}
                  py="4"
                >
                  <Flex
                    align={{ base: 'stretch', md: 'center' }}
                    justify="space-between"
                    direction={{ base: 'column', md: 'row' }}
                    gap="4"
                  >
                    <HStack align="flex-start" gap="3" minW={0}>
                      <Flex
                        boxSize="10"
                        align="center"
                        justify="center"
                        flexShrink={0}
                        borderRadius="glassInput"
                        bg="ui.surface.card"
                        borderWidth="1px"
                        borderColor="ui.border.subtle"
                        color="ui.text.primary"
                      >
                        <Bot size={18} />
                      </Flex>
                      <Box minW={0}>
                        <HStack gap="2" flexWrap="wrap">
                          <Text fontSize="sm" fontWeight="750" color="ui.text.primary">
                            AI CMO
                          </Text>
                          <Badge bg="accent.subtle" color="accent.fg" variant="subtle" size="sm">
                            Beta
                          </Badge>
                        </HStack>
                        <Text mt="1" maxW="720px" fontSize="sm" color="ui.text.secondary">
                          Turns connected evidence and your North Star into an approved Growth Plan,
                          coordinates specialist Background Tasks, and reports progress weekly.
                        </Text>
                        {!canManage ? (
                          <Text mt="1.5" fontSize="xs" color="ui.text.tertiary">
                            A workspace owner or admin controls this project setting.
                          </Text>
                        ) : null}
                        {aiCmoBetaError ? (
                          <Text mt="1.5" fontSize="xs" color="status.danger.fg">
                            {aiCmoBetaError}
                          </Text>
                        ) : null}
                      </Box>
                    </HStack>
                    <Switch.Root
                      checked={aiCmoBetaEnabled}
                      disabled={!hasProject || !canManage || updatingAiCmoBeta}
                      onCheckedChange={details => void setAiCmoBetaEnabled(details.checked)}
                      flexShrink={0}
                    >
                      <Switch.HiddenInput />
                      <Switch.Label fontSize="sm" fontWeight="650" color="ui.text.primary">
                        {aiCmoBetaEnabled ? 'On' : 'Off'}
                      </Switch.Label>
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                    </Switch.Root>
                  </Flex>
                </Box>
              ) : null}

              {showCmoSetup ? (
                <Box
                  borderWidth="1px"
                  borderColor="accent.muted"
                  borderRadius="8px"
                  bg="ui.surface.selected"
                  px={{ base: '4', md: '5' }}
                  py="4"
                >
                  <Flex
                    align={{ base: 'stretch', md: 'center' }}
                    justify="space-between"
                    direction={{ base: 'column', md: 'row' }}
                    gap="4"
                  >
                    <Box minW={0}>
                      <HStack gap="1.5">
                        <Text
                          fontSize="10px"
                          fontWeight="900"
                          color="accent.fg"
                          textTransform="uppercase"
                          letterSpacing="0.1em"
                        >
                          AI CMO
                        </Text>
                        <Text
                          as="span"
                          px="1.5"
                          borderRadius="full"
                          borderWidth="1px"
                          borderColor="accent.muted"
                          color="accent.fg"
                          fontSize="8px"
                          fontWeight="850"
                          lineHeight="16px"
                          textTransform="uppercase"
                          letterSpacing="0.06em"
                        >
                          Beta
                        </Text>
                        <Text
                          fontSize="10px"
                          fontWeight="900"
                          color="accent.fg"
                          textTransform="uppercase"
                          letterSpacing="0.1em"
                        >
                          · Step {cmoSetupStep} of 4
                        </Text>
                      </HStack>
                      <Heading
                        mt="1"
                        as="h2"
                        fontSize="md"
                        fontWeight="750"
                        color="ui.text.primary"
                      >
                        {cmoSetupTitle}
                      </Heading>
                      <Text mt="1" maxW="680px" fontSize="sm" color="ui.text.secondary">
                        {cmoSetupDescription}
                      </Text>
                      <HStack mt="2.5" gap="2" flexWrap="wrap">
                        <HStack gap="1.5">
                          <CheckCircle2 size={14} />
                          <Text fontSize="xs" color="ui.text.secondary">
                            {connectedCmoSourceIds.length} sources connected
                          </Text>
                        </HStack>
                        {missingPrimarySourceIds.length > 0 && !cmoStatusesLoading ? (
                          <Text fontSize="xs" color="status.warning.fg">
                            Recommended:{' '}
                            {missingPrimarySourceIds.map(id => id.toUpperCase()).join(' + ')}
                          </Text>
                        ) : null}
                      </HStack>
                      {displayedCmoSetupError ? (
                        <HStack mt="2" gap="2" flexWrap="wrap">
                          <Text fontSize="xs" color="status.danger.fg">
                            {displayedCmoSetupError}
                          </Text>
                          {cmoSetupErrorCode === 'growth_program_north_star_required' ? (
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={() =>
                                window.location.assign('/app/north-star?edit=1&cmoSetup=1')
                              }
                            >
                              Set North Star
                            </Button>
                          ) : null}
                        </HStack>
                      ) : null}
                    </Box>
                    <Button
                      bg="accent.solid"
                      color="accent.contrast"
                      loading={startingCmoAudit}
                      disabled={
                        !hasProject ||
                        (cmoAuditStarted && !cmoAuditThreadUrl) ||
                        (!cmoAuditReady && !cmoSetupActive && cmoStatusesLoading)
                      }
                      onClick={() => {
                        if (cmoAuditStarted && cmoAuditThreadUrl) {
                          window.location.assign(cmoAuditThreadUrl);
                          return;
                        }
                        if (cmoAuditReady || cmoSetupActive) {
                          window.location.assign('/app/workspace?mode=growth-program');
                          return;
                        }
                        void startCmoAudit();
                      }}
                      flexShrink={0}
                    >
                      {cmoSetupActive
                        ? 'Open Growth Plan'
                        : cmoAuditReady
                          ? 'Review Growth Plan'
                          : cmoAuditStarted
                            ? cmoAuditThreadUrl
                              ? 'View audit'
                              : 'Audit starting'
                            : cmoAuditFailed
                              ? 'Retry audit'
                              : missingPrimarySourceIds.length > 0
                                ? 'Continue with available data'
                                : 'Run full audit'}
                      {cmoAuditStarted && !cmoAuditThreadUrl ? (
                        <CheckCircle2 size={15} />
                      ) : (
                        <ArrowRight size={15} />
                      )}
                    </Button>
                  </Flex>
                </Box>
              ) : null}
            </VStack>
          </Collapsible.Content>
        </Collapsible.Root>
      ) : null}

      {activeTool?.id === 'intelligence' ? (currentProject?.intelligence_collection_linked ? null : (
        <IntelligenceSetupDialog
          open
          finalFocusEl={() => focusRef.current}
          onOpenChange={open => {
            if (!open) setActiveToolId(null);
          }}
          onConfigured={completeActiveModule}
        />
      )) : (
        <IntegrationSetupDialog
          key={activeTool?.id || 'closed'}
          tool={activeTool}
          hasProject={hasProject}
          canManage={canManage}
          open={Boolean(activeTool)}
          onboarding={onboarding}
          returnTo={returnTo}
          finalFocusEl={() => focusRef.current}
          onConfigured={completeActiveModule}
          onStatusChanged={() => setStatusRefreshToken(token => token + 1)}
          onOpenChange={open => {
            if (!open) {
              setActiveToolId(null);
              setStatusRefreshToken(token => token + 1);
            }
          }}
        />
      )}
    </VStack>
  );
}

function renderSelectedTool(
  toolId: IntegrationToolId,
  hasProject: boolean,
  canManage: boolean,
  onCustomMcpStatusChanged?: () => void
) {
  if (toolId === 'youtube') {
    if (!hasProject) return <ProjectRequiredNotice label="YouTube channels" />;
    return <YouTubeIntegration onStatusChanged={onCustomMcpStatusChanged} />;
  }
  if (isAnalyticsTool(toolId)) {
    if (!hasProject) {
      return <ProjectRequiredNotice label="analytics sources" />;
    }
    return <EnterpriseAnalyticsIntegrations selectedProvider={toolId} showCatalog={false} />;
  }

  if (toolId === 'google_workspace') {
    if (!hasProject) {
      return <ProjectRequiredNotice label="Google Workspace exports" />;
    }
    return <GoogleWorkspaceIntegration />;
  }

  if (toolId === 'openai_ads') {
    if (!hasProject) {
      return <ProjectRequiredNotice label="ChatGPT Ads" />;
    }
    return <OpenAiAdsIntegration onStatusChanged={onCustomMcpStatusChanged} />;
  }

  if (toolId === 'webflow') {
    if (!hasProject) {
      return <ProjectRequiredNotice label="Webflow CMS publishing" />;
    }
    return <WebflowIntegration />;
  }

  if (toolId === 'shopify') {
    if (!hasProject) {
      return <ProjectRequiredNotice label="Shopify" />;
    }
    return <ShopifyIntegration canManage={canManage} />;
  }

  if (toolId === 'wordpress') {
    if (!hasProject) {
      return <ProjectRequiredNotice label="WordPress.com delivery" />;
    }
    return <WordPressIntegration />;
  }

  if (toolId === 'github') {
    if (!hasProject) return <ProjectRequiredNotice label="Website via GitHub" />;
    return (
      <GitHubAstroIntegration canManage={canManage} onStatusChanged={onCustomMcpStatusChanged} />
    );
  }

  if (toolId === 'custom_mcp') {
    if (!hasProject) {
      return <ProjectRequiredNotice label="Custom MCP integrations" />;
    }
    return <CustomMcpIntegration onStatusChanged={onCustomMcpStatusChanged} />;
  }

  if (toolId === 'slack') {
    return (
      <SlackAgentIntegration canManage={canManage} onStatusChanged={onCustomMcpStatusChanged} />
    );
  }

  return <AttuneMcpIntegration />;
}

function IntegrationToolRow({
  tool,
  status,
  projectMissing,
  onOpen,
}: {
  tool: IntegrationTool;
  status: CatalogStatus;
  projectMissing: boolean;
  onOpen: () => void;
}) {
  const displayStatus = projectMissing
    ? { label: 'Project required', tone: 'blocked' as const }
    : status;
  return (
    <CapabilityCatalogItem
      icon={tool.icon}
      title={tool.title}
      description={tool.description}
      onOpen={onOpen}
      label={`Open ${tool.title} integration settings`}
      meta={
        <>
          <CapabilityStatus
            label={displayStatus.label}
            color={STATUS_META[displayStatus.tone].dot}
          />
          {tool.badge ? <span>{tool.badge}</span> : null}
        </>
      }
    />
  );
}

function IntegrationSetupDialog({
  tool,
  hasProject,
  canManage,
  open,
  onOpenChange,
  onStatusChanged,
  onboarding = false,
  returnTo,
  onConfigured,
  finalFocusEl,
}: {
  tool: IntegrationTool | null;
  hasProject: boolean;
  canManage: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStatusChanged: () => void;
  onboarding?: boolean;
  returnTo?: string;
  onConfigured?: () => void;
  finalFocusEl?: () => HTMLElement | null;
}) {
  if (!tool) return null;

  const projectMissing = Boolean(tool.requiresProject && !hasProject);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={event => onOpenChange(event.open)}
      size="xl"
      finalFocusEl={finalFocusEl}
    >
      <Portal>
        <Dialog.Backdrop bg="ui.overlay.backdrop" backdropFilter="blur(4px)" />
        <Dialog.Positioner p={{ base: '3', md: '6' }}>
          <Dialog.Content
            layerStyle="drawerSurface"
            display="flex"
            flexDirection="column"
            maxW={{ base: '100%', md: '760px', xl: '880px' }}
            maxH="88dvh"
            overflow="hidden"
            borderRadius="8px"
            borderWidth="1px"
            borderStyle="solid"
            borderColor="ui.border.default"
          >
            <Dialog.Header layerStyle="drawerHeader" position="relative" pr="12" py="4">
              <HStack align="center" gap="3" minW={0}>
                <Box
                  boxSize="10"
                  borderRadius="glassButton"
                  bg="ui.surface.drawerMuted"
                  color="ui.text.primary"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  {tool.icon}
                </Box>
                <Box minW={0}>
                  <HStack gap="2" flexWrap="wrap">
                    <Dialog.Title color="ui.text.primary" fontSize="lg" fontWeight="700">
                      {tool.title}
                    </Dialog.Title>
                    <Badge
                      bg={projectMissing ? 'status.warning.bg' : 'ui.surface.selected'}
                      color={projectMissing ? 'status.warning.fg' : 'ui.text.secondary'}
                      variant="subtle"
                    >
                      {projectMissing ? 'Project required' : tool.auth}
                    </Badge>
                    {tool.badge ? (
                      <Badge bg="accent.subtle" color="accent.fg" variant="subtle">
                        {tool.badge}
                      </Badge>
                    ) : null}
                  </HStack>
                  <Dialog.Description
                    color="ui.text.secondary"
                    fontSize="sm"
                    mt="0.5"
                    lineClamp={1}
                  >
                    {tool.description}
                  </Dialog.Description>
                </Box>
              </HStack>
              <Dialog.CloseTrigger asChild position="absolute" top="3" right="3">
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Header>
            <Dialog.Body
              bg="ui.surface.drawer"
              flex="1"
              minH="0"
              overflowY="auto"
              px={{ base: '4', md: '6' }}
              py="4"
            >
              <IntegrationSurfaceProvider mode="flat">
                <IntegrationSetupProvider
                  embedded
                  onboarding={onboarding}
                  returnTo={returnTo}
                  onConfigured={onConfigured}
                >
                  {renderSelectedTool(tool.id, hasProject, canManage, onStatusChanged)}
                </IntegrationSetupProvider>
              </IntegrationSurfaceProvider>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

function ProjectRequiredNotice({ label }: { label: string }) {
  return (
    <Box
      p="3"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="status.warning.border"
      bg="status.warning.bg"
      borderRadius="glass"
    >
      <HStack gap={2}>
        <AlertCircle size={16} color="var(--chakra-colors-orange-500)" />
        <Text color="status.warning.fg">Select a project to configure {label}.</Text>
      </HStack>
    </Box>
  );
}
